import { useRef, useState } from "react";
import { UploadCloud, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const DEFAULT_ACCEPT = ".pdf,.doc,.docx,.txt,.rtf,.odt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MAX_MB = 15;

function prettySize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export default function FileUploadDropzone({
  folder = "documents",
  accept = DEFAULT_ACCEPT,
  currentUrl = "",
  currentName = "",
  label = "Drop PDF or DOC here",
  helpText = "PDF, DOC, DOCX, TXT · up to 15MB",
  onUploaded,
  onCleared,
  testId = "doc-dropzone",
  compact = false,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedName, setUploadedName] = useState(currentName);
  const [uploadedSize, setUploadedSize] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState(currentUrl);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB}MB.`);
      return;
    }
    const form = new FormData();
    form.append("file", file);
    form.append("folder", folder);
    setUploading(true);
    setProgress(0);
    try {
      const res = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setUploadedName(file.name);
      setUploadedSize(file.size);
      setUploadedUrl(res.data?.url || "");
      toast.success("File uploaded");
      onUploaded?.({ ...res.data, original_filename: file.name, size: file.size });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const clear = () => {
    setUploadedName("");
    setUploadedSize(0);
    setUploadedUrl("");
    if (inputRef.current) inputRef.current.value = "";
    onCleared?.();
  };

  const hasFile = !!uploadedUrl || !!uploadedName;

  return (
    <div data-testid={testId} className="space-y-2">
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative cursor-pointer rounded-lg border-2 border-dashed transition-colors p-${compact ? "3" : "5"} flex flex-col items-center justify-center text-center ${
          uploading
            ? "border-[#00E5FF]/60 bg-[#00E5FF]/5"
            : dragging
            ? "border-[#00E5FF] bg-[#00E5FF]/10"
            : "border-white/15 hover:border-[#00E5FF]/50 hover:bg-white/[0.02]"
        }`}
        data-testid={`${testId}-drop`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
        {uploading ? (
          <>
            <Loader2 className="text-[#00E5FF] animate-spin mb-2" size={compact ? 20 : 28} />
            <div className="text-sm text-slate-200">Uploading… {progress}%</div>
            <div className="w-full mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden max-w-xs">
              <div className="h-full bg-[#00E5FF] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : (
          <>
            <UploadCloud className={`mb-2 ${dragging ? "text-[#00E5FF]" : "text-slate-400"}`} size={compact ? 20 : 28} />
            <div className={`${compact ? "text-xs" : "text-sm"} font-semibold text-white`}>{label}</div>
            <div className="text-[11px] text-slate-400 mt-1">{helpText}</div>
            <div className="text-[11px] text-[#00E5FF] mt-1">or click to browse</div>
          </>
        )}
      </div>

      {hasFile && !uploading && (
        <div className="flex items-center gap-2 bg-[#050A15] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid={`${testId}-current`}>
          <FileText size={14} className="text-[#00E5FF] shrink-0" />
          <div className="flex-1 min-w-0">
            {uploadedUrl ? (
              <a href={uploadedUrl} target="_blank" rel="noreferrer" className="text-white hover:text-[#00E5FF] truncate block" data-testid={`${testId}-link`}>
                {uploadedName || "Uploaded file"}
              </a>
            ) : (
              <span className="text-white truncate block">{uploadedName}</span>
            )}
            {uploadedSize ? <div className="text-[10px] text-slate-500">{prettySize(uploadedSize)}</div> : null}
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-slate-400 hover:text-red-300 p-1"
            title="Remove file"
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
